import { ApplicationInsights, ICustomProperties } from '@microsoft/applicationinsights-web';

/**
 * Singleton Application Insights instance.
 *
 * Reads the connection string from the CRA env var
 * `REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING`.
 *
 * If the variable is not set the SDK is created but stays disabled,
 * so the rest of the app can import helpers without branching.
 */

const connectionString =
    process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING || '';

const appInsights = new ApplicationInsights({
    config: {
        connectionString,
        /* ---- sensible defaults ---- */
        enableAutoRouteTracking: false, // we track routes manually via React Router
        disableFetchTracking: false,    // track outgoing fetch/XHR calls
        enableCorsCorrelation: true,    // correlate cross-origin requests
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        autoTrackPageVisitTime: true,   // track time spent on each page
        /* ---- exclude Auth0 from correlation / tracking ---- *
         * The SDK injects traceparent headers into outgoing requests.
         * Auth0 endpoints reject or mishandle those extra headers,
         * which breaks the OAuth token exchange and causes a redirect loop. */
        correlationHeaderExcludedDomains: [
            '*.auth0.com',
            'login.microsoftonline.com',
        ],
    },
});

if (connectionString) {
    appInsights.loadAppInsights();
    appInsights.addTelemetryInitializer((item) => {
        if (item.tags) {
            // Attach the app name so queries in App Insights are easier to filter
            item.tags['ai.cloud.role'] = 'content-management-frontend';
        }
        // Tag every telemetry item with the environment (DEV / PROD)
        const env = process.env.REACT_APP_ENV || 'LOCAL';
        item.data = { ...item.data, environment: env };
    });
}

/* ---- convenience helpers ---- */

/** Track a page view (called by the route-change tracker). */
export function trackPageView(name: string, uri: string) {
    if (!connectionString) return;
    appInsights.trackPageView({ name, uri });
}

/** Track a custom event (e.g. button clicks, dialog opens). */
export function trackEvent(name: string, properties?: ICustomProperties) {
    if (!connectionString) return;
    appInsights.trackEvent({ name }, properties);
}

/** Track an exception. */
export function trackException(error: Error, properties?: ICustomProperties) {
    if (!connectionString) return;
    appInsights.trackException({ exception: error }, properties);
}

/** Set the authenticated user context (call after login). */
export function setAuthenticatedUser(userId: string, accountId?: string) {
    if (!connectionString) return;
    appInsights.setAuthenticatedUserContext(userId, accountId, true);
}

/** Clear the authenticated user context (call on logout). */
export function clearAuthenticatedUser() {
    if (!connectionString) return;
    appInsights.clearAuthenticatedUserContext();
}

export default appInsights;
